import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { Badge } from '@ctw/shared/components/badge';
import { CollapsibleContent } from '@ctw/shared/components/containers/collapsible-content';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { adtMessageTitle } from '@ctw/shared/content/related-documents/adt-message-labels';
import { RelatedDocumentNarratives } from '@ctw/shared/content/related-documents/related-document-narratives';
import {
  joinWithMiddleDot,
  SourceDocumentHeader,
} from '@ctw/shared/content/related-documents/source-document-header';
import { ViewSourceDocumentButton } from '@ctw/shared/content/related-documents/view-source-document-button';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';

export type EncounterRelatedDocumentsListItem =
  | { kind: 'binary'; preferred?: boolean; document: DocumentModel }
  | {
      kind: 'adt';
      document: DocumentModel;
      messageType?: string;
      effectiveTimeDisplay?: string;
      locationDisplay?: string;
    };

export interface EncounterRelatedDocumentsListProps {
  isLoading: boolean;
  isError?: boolean;
  items: Array<EncounterRelatedDocumentsListItem>;
}

export const EncounterRelatedDocumentsList = ({
  isLoading,
  isError = false,
  items,
}: EncounterRelatedDocumentsListProps) => {
  if (isLoading) {
    return (
      <div className={tw`flex w-full justify-center`}>
        <LoadingSpinner className={tw`text-content-icon`} message="Loading clinical documents" />
      </div>
    );
  }
  if (items.length > 0 || isError) {
    return (
      <CollapsibleContent
        label="Source Documents"
        secondaryLabel={`${items.length} ${pluralize('document', items.length)}`}
        initiallyOpen={false}
      >
        <div className={tw`space-y-4`}>
          {items.map((item) => (
            <div
              key={item.document.id}
              className={tw`flex flex-col gap-2 divide-y divide-divider-main rounded-lg border bg-white p-3 text-left`}
            >
              <div className={tw`flex items-center justify-between gap-2`}>
                <div className={tw`flex min-w-0 flex-1 items-center gap-2`}>
                  <SourceDocumentHeader
                    title={
                      item.kind === 'adt' ? adtMessageTitle(item.messageType) : item.document.title
                    }
                    clinicalDateDisplay={
                      item.kind === 'adt' ? item.effectiveTimeDisplay : item.document.dateCreated
                    }
                    badgeLabel={item.kind === 'adt' ? 'ADT' : item.document.badgeLabel}
                    sourceLine={
                      item.kind === 'adt'
                        ? joinWithMiddleDot(
                            'ADT feed',
                            item.document.networkDisplay,
                            item.locationDisplay,
                          )
                        : joinWithMiddleDot(
                            item.document.networkDisplay,
                            item.document.providerDisplay,
                          )
                    }
                    receivedDisplay={item.document.createdAtDisplay}
                  />
                  {item.kind === 'binary' && item.preferred && (
                    <Badge variant="meta" content="Documents" />
                  )}
                </div>
                <div className={tw`flex shrink-0 items-center justify-center`}>
                  {item.kind === 'binary' ? (
                    <ResponsiveSourceDocumentLink
                      model={item.document}
                      binaryId={item.document.binaryId}
                    />
                  ) : (
                    <ViewSourceDocumentButton
                      resourceType="DocumentReference"
                      resourceId={item.document.id}
                      contentType={item.document.contentType}
                      enabled={true}
                    />
                  )}
                </div>
              </div>
              {item.kind === 'binary' && item.document.binaryId && (
                <RelatedDocumentNarratives binaryId={item.document.binaryId} />
              )}
            </div>
          ))}
          {isError && (
            <div
              className={tw`flex items-center gap-2 rounded-md border border-border-main px-3 py-4 text-content-subtle shadow-sm`}
            >
              <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
              <div className={tw`font-medium text-[1rem] tracking-wide`}>
                Unable to load these documents
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    );
  }

  return <></>;
};
